import {
  useGetProduct,
  type ProductApplicabilityState,
} from '@/shared/hooks/api/product'
import { useGetEmcProductComplianceFiles } from '@/shared/hooks/api/emcComplianceFiles'
import { useGetEmcComplianceFileSlot } from '@/shared/hooks/api/emcProductComplianceFileAttachments'
import { useGetLvdProductComplianceFiles } from '@/shared/hooks/api/lvdComplianceFiles'
import { useGetLvdComplianceFileSlot } from '@/shared/hooks/api/lvdProductComplianceFileAttachments'
import {
  useGetProductSharedEvidence,
} from '@/shared/hooks/api/productSharedEvidence'
import { useGetRohsProductComplianceFiles } from '@/shared/hooks/api/rohsComplianceFiles'
import {
  useGetProductDrawings,
} from '@/shared/hooks/api/rohsProductDrawings'
import {
  buildProductRegulationsJourneyByLaw,
  type ProductRegulationsJourneyByLaw,
} from '../utils/buildProductRegulationsJourneyByLaw'

function isLawApplicable(
  applicability: ProductApplicabilityState | undefined,
  law: 'rohs' | 'lvd' | 'emc'
): boolean {
  return applicability?.[law]?.status === 'APPLICABLE'
}

export function useProductRegulationsJourneyByLaw(
  productId: string,
  applicability: ProductApplicabilityState | undefined
): {
  journeyByLaw: ProductRegulationsJourneyByLaw
  isLoading: boolean
} {
  const rohsApplies = isLawApplicable(applicability, 'rohs')
  const lvdApplies = isLawApplicable(applicability, 'lvd')
  const emcApplies = isLawApplicable(applicability, 'emc')

  const { data: product, isLoading: isLoadingProduct } = useGetProduct(productId)
  const { data: sharedEvidence, isLoading: isLoadingSharedEvidence } =
    useGetProductSharedEvidence(productId)
  const { data: rohsDrawings, isLoading: isLoadingRohsDrawings } =
    useGetProductDrawings(productId, { enabled: rohsApplies })
  const { data: rohsFiles, isLoading: isLoadingRohsFiles } =
    useGetRohsProductComplianceFiles(productId, { enabled: rohsApplies })
  const { data: lvdFiles, isLoading: isLoadingLvdFiles } =
    useGetLvdProductComplianceFiles(productId, { enabled: lvdApplies })
  const { data: emcFiles, isLoading: isLoadingEmcFiles } =
    useGetEmcProductComplianceFiles(productId, { enabled: emcApplies })

  const lvdDrawings = useGetLvdComplianceFileSlot(productId, 'DRAWINGS_SCHEMAS', {
    enabled: lvdApplies,
  })
  const lvdStandardsTestReport = useGetLvdComplianceFileSlot(
    productId,
    'STANDARDS_TEST_REPORT',
    { enabled: lvdApplies }
  )
  const lvdExaminations = useGetLvdComplianceFileSlot(
    productId,
    'EXAMINATIONS_OR_CALCULATIONS',
    { enabled: lvdApplies }
  )
  const lvdInstructions = useGetLvdComplianceFileSlot(
    productId,
    'INSTRUCTIONS_AND_SAFETY_INFORMATION',
    { enabled: lvdApplies }
  )

  const emcDrawings = useGetEmcComplianceFileSlot(productId, 'DRAWINGS_SCHEMAS', {
    enabled: emcApplies,
  })
  const emcDisturbance = useGetEmcComplianceFileSlot(
    productId,
    'TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE',
    { enabled: emcApplies }
  )
  const emcImmunity = useGetEmcComplianceFileSlot(
    productId,
    'TEST_REPORT_ELECTROMAGNETIC_IMMUNITY',
    { enabled: emcApplies }
  )
  const emcDescriptions = useGetEmcComplianceFileSlot(
    productId,
    'DESCRIPTIONS_AND_EXPLANATIONS',
    { enabled: emcApplies }
  )

  const lvdSlotQueries = [
    lvdDrawings,
    lvdStandardsTestReport,
    lvdExaminations,
    lvdInstructions,
  ]
  const emcSlotQueries = [emcDrawings, emcDisturbance, emcImmunity, emcDescriptions]

  const isLoading =
    isLoadingProduct ||
    isLoadingSharedEvidence ||
    (rohsApplies &&
      (isLoadingRohsDrawings || isLoadingRohsFiles)) ||
    (lvdApplies &&
      (isLoadingLvdFiles || lvdSlotQueries.some((query) => query.isLoading))) ||
    (emcApplies &&
      (isLoadingEmcFiles || emcSlotQueries.some((query) => query.isLoading)))

  const journeyByLaw = buildProductRegulationsJourneyByLaw({
    productDescription: product?.description,
    sharedEvidence,
    rohsApplies,
    rohsDrawings,
    rohsFiles,
    lvdApplies,
    lvdFiles,
    lvdSlotAttachments: {
      DRAWINGS_SCHEMAS: lvdDrawings.data,
      STANDARDS_TEST_REPORT: lvdStandardsTestReport.data,
      EXAMINATIONS_OR_CALCULATIONS: lvdExaminations.data,
      INSTRUCTIONS_AND_SAFETY_INFORMATION: lvdInstructions.data,
    },
    emcApplies,
    emcFiles,
    emcSlotAttachments: {
      DRAWINGS_SCHEMAS: emcDrawings.data,
      TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE: emcDisturbance.data,
      TEST_REPORT_ELECTROMAGNETIC_IMMUNITY: emcImmunity.data,
      DESCRIPTIONS_AND_EXPLANATIONS: emcDescriptions.data,
    },
  })

  return { journeyByLaw, isLoading }
}
